import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '@/lib/useApi';
import HomeownerDashboard from '../HomeownerDashboard';

interface Project {
  id: string;
  address: string;
  status: string;
  homeownerEmail: string;
  updatedAt: string;
}

interface ReportRequest {
  id: string;
  status: string;
  reportUrl?: string;
  requestedAt: string;
}

interface ManualBid {
  id: string;
  contractorName: string;
  contractorEmail?: string;
  contractorPhone?: string;
  licenseNumber?: string;
  priceCents: number;
  paymentTerms: string;
  timeline: string;
  startDate?: string;
  notes?: string;
  attachmentUrl?: string;
  isWinner: boolean;
  createdAt: string;
}

interface AdminHomeownerDetailProps {
  homeownerId: string;
  onNavigate: (screen: string, params?: any) => void;
}

// COREBIDS FIX: admin homeowner detail with controls
export default function AdminHomeownerDetail({ homeownerId, onNavigate }: AdminHomeownerDetailProps) {
  const { getToken } = useAuth();
  const api = useApi();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectDetails, setProjectDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load homeowner profile for editing (email/phone/address)
  const [homeownerProfile, setHomeownerProfile] = useState<any>(null);

  // Form states
  const [newBid, setNewBid] = useState({
    contractorName: '',
    price: '',
    paymentTerms: '',
    timeline: '',
    startDate: '',
    notes: '',
    attachmentUrl: '',
    contractorEmail: '',
    contractorPhone: '',
    licenseNumber: ''
  });
  const [reportUrl, setReportUrl] = useState('');
  const [projectStatus, setProjectStatus] = useState('');

  useEffect(() => {
    fetchProjects();
    (async () => {
      try {
        const d = await api.get(`/api/admin/homeowners/${homeownerId}`);
        setHomeownerProfile(d);
      } catch {}
    })();
  }, [homeownerId]);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails();
    }
  }, [selectedProject]);

  // CB:BEGIN admin-homeowner-detail-response
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const resp = await api.get(`/api/admin/projects?homeownerId=${homeownerId}`);
      const list = Array.isArray(resp?.projects) ? resp.projects : (Array.isArray(resp) ? resp : []);
      setProjects(list);
      
      if (list.length > 0) {
        setSelectedProject(list[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async () => {
    if (!selectedProject) return;
    
    try {
      const data = await api.get(`/api/admin/projects/${selectedProject.id}`);
      setProjectDetails(data);
      setProjectStatus((data?.project?.status) || data?.status || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };
  // CB:END admin-homeowner-detail-response

  const handleCreateBid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      await api.post('/api/admin/manual-bids', { projectId: selectedProject.id, ...newBid });

      // Reset form
      setNewBid({
        contractorName: '',
        price: '',
        paymentTerms: '',
        timeline: '',
        startDate: '',
        notes: '',
        attachmentUrl: '',
        contractorEmail: '',
        contractorPhone: '',
        licenseNumber: ''
      });

      // Refresh project details
      fetchProjectDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleMarkWinner = async (bidId: string) => {
    if (!selectedProject) return;

    try {
      await api.post(`/api/admin/manual-bids/${bidId}/win`);

      fetchProjectDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDeleteBid = async (bidId: string) => {
    if (!selectedProject) return;

    try {
      await api.del(`/api/admin/manual-bids/${bidId}`);

      fetchProjectDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleAttachReport = async () => {
    if (!projectDetails?.reportRequest?.id || !reportUrl) return;

    try {
      await api.post(`/api/admin/report-requests/${projectDetails.reportRequest.id}/attach`, { reportUrl });

      setReportUrl('');
      fetchProjectDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedProject || !projectStatus) return;

    try {
      await api.post(`/api/admin/projects/${selectedProject.id}/status`, { status: projectStatus });

      fetchProjectDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSaveHomeowner = async () => {
    try {
      if (!homeownerProfile) return;
      await api.put(`/api/admin/homeowners/${homeownerId}`, {
        email: homeownerProfile?.user?.email,
        phone: homeownerProfile?.user?.phone,
        address: homeownerProfile?.address
      });
      const d = await api.get(`/api/admin/homeowners/${homeownerId}`);
      setHomeownerProfile(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const formatPrice = (priceCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(priceCents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* COREBIDS FIX: admin header with back button */}
      <div className="mb-6">
        <button
          onClick={() => onNavigate('admin-homeowners-list')}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          ← Back to Homeowners
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Homeowner Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* COREBIDS FIX: left column - homeowner dashboard */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Homeowner View</h2>
          {selectedProject && (
            <div className="bg-gray-50 rounded-lg p-4">
              <HomeownerDashboard 
                projectId={selectedProject.id} 
                adminView={true}
              />
            </div>
          )}
        </div>

        {/* COREBIDS FIX: right column - admin controls */}
        <div className="space-y-6">
          {/* Homeowner Edit */}
          {homeownerProfile && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Homeowner</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={homeownerProfile?.user?.email || ''}
                    onChange={(e) => setHomeownerProfile((p: any) => ({ ...p, user: { ...(p?.user||{}), email: e.target.value } }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={homeownerProfile?.user?.phone || ''}
                    onChange={(e) => setHomeownerProfile((p: any) => ({ ...p, user: { ...(p?.user||{}), phone: e.target.value } }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={homeownerProfile?.address || ''}
                    onChange={(e) => setHomeownerProfile((p: any) => ({ ...p, address: e.target.value }))}
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={handleSaveHomeowner} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save Changes</button>
                </div>
              </div>
            </div>
          )}
          {/* Project Selection */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Project</h3>
            <select
              value={selectedProject?.id || ''}
              onChange={(e) => {
                const project = projects.find(p => p.id === e.target.value);
                setSelectedProject(project || null);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a project...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.address} ({project.status})
                </option>
              ))}
            </select>
          </div>

          {/* Report Controls */}
          {projectDetails?.reportRequest && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Controls</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attach Report URL
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="url"
                      value={reportUrl}
                      onChange={(e) => setReportUrl(e.target.value)}
                      placeholder="https://example.com/report.pdf"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleAttachReport}
                      disabled={!reportUrl}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Attach
                    </button>
                  </div>
                </div>
                
                {projectDetails.reportRequest.reportUrl && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Current Report:</p>
                    <a 
                      href={projectDetails.reportRequest.reportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      View Report
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manual Bids */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Bids</h3>
            
            {/* Add Bid Form */}
            <form onSubmit={handleCreateBid} className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contractor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newBid.contractorName}
                    onChange={(e) => setNewBid({...newBid, contractorName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (USD) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={newBid.price}
                    onChange={(e) => setNewBid({...newBid, price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms *
                  </label>
                  <input
                    type="text"
                    required
                    value={newBid.paymentTerms}
                    onChange={(e) => setNewBid({...newBid, paymentTerms: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timeline *
                  </label>
                  <input
                    type="text"
                    required
                    value={newBid.timeline}
                    onChange={(e) => setNewBid({...newBid, timeline: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newBid.startDate}
                    onChange={(e) => setNewBid({...newBid, startDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newBid.contractorEmail}
                    onChange={(e) => setNewBid({...newBid, contractorEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={newBid.notes}
                  onChange={(e) => setNewBid({...newBid, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <button
                type="submit"
                className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Add Manual Bid
              </button>
            </form>

            {/* Existing Bids */}
            {projectDetails?.manualBids && projectDetails.manualBids.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Existing Bids</h4>
                {projectDetails.manualBids.map((bid: ManualBid) => (
                  <div key={bid.id} className={`border rounded-lg p-4 ${
                    bid.isWinner ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">{bid.contractorName}</h5>
                      <span className="text-lg font-bold text-gray-900">{formatPrice(bid.priceCents)}</span>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Payment:</strong> {bid.paymentTerms}</p>
                      <p><strong>Timeline:</strong> {bid.timeline}</p>
                      {bid.startDate && <p><strong>Start:</strong> {formatDate(bid.startDate)}</p>}
                      {bid.notes && <p><strong>Notes:</strong> {bid.notes}</p>}
                    </div>
                    
                    <div className="flex space-x-2 mt-3">
                      {!bid.isWinner && (
                        <button
                          onClick={() => handleMarkWinner(bid.id)}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          Mark Winner
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteBid(bid.id)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status Controls */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Controls</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Status
                </label>
                <div className="flex space-x-2">
                  <select
                    value={projectStatus}
                    onChange={(e) => setProjectStatus(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="REQUESTED">Requested</option>
                    <option value="REPORT_ORDERED">Report Ordered</option>
                    <option value="BIDS_COLLECTING">Bids Collecting</option>
                    <option value="BIDS_READY">Bids Ready</option>
                    <option value="BID_SELECTED">Bid Selected</option>
                    <option value="ACTIVE">Active</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                  <button
                    onClick={handleUpdateStatus}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

